import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FlightSearchWidget from "@/components/FlightSearchWidget";
import { api, AIRLINE_LOGOS, formatDuration, formatPrice } from "@/lib/api";
import { AirplaneInFlight, Funnel, ArrowRight, Phone, Headset, CaretDown, CalendarBlank, Suitcase, Armchair, Tag, Clock, Sparkle } from "@phosphor-icons/react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";
import { useSEO } from "@/lib/seo";
import MobileSearchSummary from "@/components/results/MobileSearchSummary";
import AirlinePriceRail from "@/components/results/AirlinePriceRail";
import MobileFilterPills from "@/components/results/MobileFilterPills";
import MobileFlightCard from "@/components/results/MobileFlightCard";

function formatDateLong(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

function cabinLabel(c) {
  return (c || "").replace("_", " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function DetailRow({ icon: I, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <I size={16} className="text-[#C29B62] mt-0.5 shrink-0" weight="duotone" />
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase text-[#666460]">{label}</p>
        <p className="text-sm text-[#1C1C1C] mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function FlightCard({ flight, onSelect, leg, baseline, baselineId }) {
  const [open, setOpen] = useState(false);
  // Delta-pricing mode is used in the return list when an outbound is locked.
  // We show the price difference vs the selected outbound's offer total
  // instead of the absolute fare — same UX pattern as CheapOair.
  const isDelta = typeof baseline === "number";
  const isIncluded = isDelta && baselineId === flight.id;
  const delta = isDelta ? flight.price - baseline : 0;
  return (
    <div className="bg-white border border-[#E0DDD6] lift" data-testid={`flight-card-${flight.id}`}>
      <div className="px-4 md:px-5 py-3.5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-2 flex items-center gap-2.5">
          <div className="w-10 h-10 bg-[#F7F5F0] flex items-center justify-center border border-[#E0DDD6] shrink-0">
            <img src={AIRLINE_LOGOS[flight.airline_code]} alt={flight.airline_name} className="w-8 h-8 object-contain" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#1C1C1C] truncate leading-tight">{flight.airline_name}</p>
            <p className="text-xs text-[#666460] leading-tight">{flight.flight_number}</p>
          </div>
        </div>

        <div className="md:col-span-6 flex items-center justify-between">
          <div className="text-left">
            <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight leading-none">{flight.depart_time}</p>
            <p className="text-sm font-semibold text-[#1C1C1C] mt-1.5">{flight.origin}</p>
          </div>
          <div className="flex-1 px-4">
            <p className="text-xs text-[#666460] text-center mb-1 leading-none">{formatDuration(flight.duration_minutes)}</p>
            <div className="relative h-px bg-[#E0DDD6]">
              <AirplaneInFlight size={14} className="absolute left-1/2 -translate-x-1/2 -top-[7px] text-[#C29B62]" weight="duotone" />
            </div>
            <p className="text-xs text-[#666460] text-center mt-1 leading-none">{flight.stops === 0 ? "Non-stop" : `${flight.stops} stop`}</p>
          </div>
          <div className="text-right">
            <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight leading-none">{flight.arrive_time}</p>
            <p className="text-sm font-semibold text-[#1C1C1C] mt-1.5">{flight.destination}</p>
          </div>
        </div>

        <div className="md:col-span-4 flex md:flex-row items-center md:items-center justify-between gap-3 md:gap-3 md:border-l md:border-[#E0DDD6] md:pl-4">
          <div className="text-left md:text-right md:flex-1 min-w-0">
            {isDelta ? (
              isIncluded ? (
                <p className="font-serif text-xl tracking-tight text-emerald-600 leading-none" data-testid={`price-delta-${flight.id}`}>
                  Included
                </p>
              ) : (
                <p
                  className={`font-serif text-2xl tracking-tight leading-none ${
                    delta > 0 ? "text-rose-600" : "text-emerald-600"
                  }`}
                  data-testid={`price-delta-${flight.id}`}
                >
                  {delta > 0 ? "+" : "−"}{formatPrice(Math.abs(delta))}
                </p>
              )
            ) : (
              <p className="font-serif text-2xl md:text-3xl leading-none" style={{ color: "#C29B62" }}>{formatPrice(flight.price)}</p>
            )}
            <p className="text-[11px] text-[#666460] mt-1.5 truncate">
              {flight.fare_class} · {flight.cabin}
              {flight.baggage_summary ? ` · ${flight.baggage_summary}` : ""}
              {isDelta && !isIncluded ? ` · Total ${formatPrice(flight.price)}` : ""}
            </p>
          </div>
          <button
            onClick={() => onSelect(flight, leg)}
            className="btn-gold text-xs tracking-[0.16em] uppercase px-4 py-2 shrink-0"
            data-testid={`select-${flight.id}`}
          >
            Select <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* Details toggle — slim secondary bar */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`flight-details-toggle-${flight.id}`}
        className="w-full border-t border-[#E0DDD6] px-4 md:px-5 py-1.5 flex items-center justify-between text-[9px] tracking-[0.16em] uppercase text-[#A09E9A] hover:text-[#C29B62] hover:bg-[#F7F5F0] transition-colors"
      >
        <span>{open ? "Hide flight details" : "Show flight details"}</span>
        <CaretDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 md:px-6 pb-6 pt-2 border-t border-[#E0DDD6] bg-[#F7F5F0]" data-testid={`flight-details-${flight.id}`}>
          {/* Itinerary banner */}
          <div className="bg-white border border-[#E0DDD6] p-5 md:p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              <div className="md:col-span-4">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#C29B62] mb-1">Depart · {flight.origin}</p>
                <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight">{flight.depart_time}</p>
                <p className="text-xs text-[#666460] mt-1">{formatDateLong(flight.depart_date)}</p>
              </div>
              <div className="md:col-span-4 text-center">
                <p className="text-xs text-[#666460]">{formatDuration(flight.duration_minutes)}</p>
                <div className="relative h-px bg-[#E0DDD6] my-2">
                  <AirplaneInFlight size={18} className="absolute left-1/2 -translate-x-1/2 -top-2 text-[#C29B62]" weight="duotone" />
                </div>
                <p className="text-xs text-[#666460]">{flight.stops === 0 ? "Non-stop" : `${flight.stops} stop${flight.stops > 1 ? "s" : ""}`}</p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#C29B62] mb-1">Arrive · {flight.destination}</p>
                <p className="font-serif text-2xl text-[#1C1C1C] tracking-tight">{flight.arrive_time}</p>
                <p className="text-xs text-[#666460] mt-1">Local time at {flight.destination}</p>
              </div>
            </div>
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-5">
            <DetailRow icon={Tag}           label="Airline"     value={`${flight.airline_name} (${flight.airline_code})`} />
            <DetailRow icon={AirplaneInFlight} label="Flight"   value={flight.flight_number} />
            <DetailRow icon={Armchair}      label="Cabin"       value={cabinLabel(flight.cabin)} />
            <DetailRow icon={Tag}           label="Fare class"  value={flight.fare_class} />
            <DetailRow icon={CalendarBlank} label="Depart date" value={formatDateLong(flight.depart_date)} />
            <DetailRow icon={Clock}         label="Duration"    value={formatDuration(flight.duration_minutes)} />
            <DetailRow icon={Suitcase}      label="Baggage"     value={flight.baggage_summary || (flight.baggage_included ? "Included" : "Hand baggage only")} />
            <DetailRow icon={AirplaneInFlight} label="Stops"    value={flight.stops === 0 ? "Non-stop" : `${flight.stops} stop`} />
          </div>

          {/* Concierge hint */}
          <div className="mt-5 pt-4 border-t border-[#E0DDD6] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-xs text-[#666460] leading-relaxed">
              Need help understanding the fare rules, requesting a paid upgrade, or building a multi-city itinerary around this leg?
              Our travel desk has access to fares not shown online.
            </p>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid={`flight-details-call-${flight.id}`}
              className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#C29B62] hover:text-[#A88550] whitespace-nowrap"
            >
              <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SearchResults() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stops, setStops] = useState({ 0: true, 1: true });
  const [airlines, setAirlines] = useState({});
  const [priceMax, setPriceMax] = useState(5000);
  const [sort, setSort] = useState("price");

  const query = useMemo(() => {
    const adults = Math.max(1, parseInt(params.get("adults") || "0", 10) || 0);
    const children = Math.max(0, parseInt(params.get("children") || "0", 10) || 0);
    const infants = Math.max(0, parseInt(params.get("infants") || "0", 10) || 0);
    // Back-compat: older links only have ?passengers=N → treat them all as adults
    const legacy = parseInt(params.get("passengers") || "0", 10) || 0;
    const a = adults || (legacy || 1);
    const total = adults || children || infants ? adults + children + infants : a;
    return {
      trip_type: params.get("trip_type") || "round_trip",
      origin: params.get("origin") || "JFK",
      destination: params.get("destination") || "LAX",
      depart_date: params.get("depart_date") || new Date(Date.now() + 21*86400000).toISOString().slice(0,10),
      return_date: params.get("return_date"),
      adults: a,
      children,
      infants,
      passengers: total,
      cabin: params.get("cabin") || "economy",
    };
  }, [params]);

  useSEO({
    title: `Flights ${query.origin} → ${query.destination} | TheCityPair`,
    description: `Premium flight options for ${query.origin} → ${query.destination}. Talk to a real specialist for unpublished fares.`,
    noindex: true,
  });

  useEffect(() => {
    setLoading(true); setError(null);
    api.post("/products/flights/search", query)
      .then((r) => {
        setData(r.data);
        const all = [...(r.data.outbound || []), ...(r.data.return || [])];
        const max = Math.max(...all.map(f => f.price), 1000);
        setPriceMax(Math.ceil(max));
        const al = {}; all.forEach(f => { al[f.airline_code] = true; });
        setAirlines(al);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [query]);

  const filterAndSort = (list) => {
    let r = list.filter(f => stops[f.stops] && airlines[f.airline_code] !== false && f.price <= priceMax);
    if (sort === "price") r.sort((a,b) => a.price - b.price);
    if (sort === "duration") r.sort((a,b) => a.duration_minutes - b.duration_minutes);
    if (sort === "depart") r.sort((a,b) => a.depart_time.localeCompare(b.depart_time));
    return r;
  };

  // Commit a (possibly paired) selection to sessionStorage and navigate to /booking.
  //
  // Important: every flight card in our search results represents one slice
  // of a complete paired round-trip offer from Duffel — `flight.price` is the
  // full RT total for that offer. So:
  //   • paired (same offer_id) → price = the shared offer total (unchanged)
  //   • non-paired             → user is effectively switching to the RETURN
  //     option's offer; its price is what they pay total. Outbound is just
  //     a "preferred time/style" hint at that point.
  // This corrects an older bug that summed the two cards (= double-counting).
  const commitToBooking = (outboundFlight, returnFlight, paired = false, pairedTotal = null) => {
    const paxBreakdown = {
      adults: query.adults,
      children: query.children,
      infants: query.infants,
    };
    const finalPrice = paired
      ? Number(pairedTotal ?? outboundFlight.price)
      : Number(returnFlight?.price || outboundFlight?.price || 0);
    sessionStorage.setItem("tcp_selected_flight", JSON.stringify({
      ...outboundFlight,
      price: finalPrice,
      passengers: query.passengers,
      pax_breakdown: paxBreakdown,
      return_date: query.return_date,
      return_leg: returnFlight,
      paired,
    }));
    nav("/booking");
  };

  // Find the cheapest paired round-trip offer on the SAME airline as the
  // user's selection. Returns null if there is none cheaper than the sum.
  // Algorithm: each search result card carries `id` which is the parent
  // Duffel offer id. Duffel returns one offer per paired itinerary, so the
  // cheapest outbound card matching the airline IS the cheapest paired
  // offer for that airline (its price equals the offer's total_amount).
  const findCheaperPairedOffer = (sel_out, sel_ret) => {
    if (!data || !sel_out || !sel_ret) return null;
    if (sel_out.airline_code !== sel_ret.airline_code) return null;
    if (sel_out.id === sel_ret.id) return null; // already paired
    const sum = (sel_out.price || 0) + (sel_ret.price || 0);
    const outboundOnAirline = (data.outbound || []).filter(
      (f) => f.airline_code === sel_out.airline_code,
    );
    if (outboundOnAirline.length === 0) return null;
    const cheapest = outboundOnAirline.reduce(
      (m, f) => (f.price < m.price ? f : m),
      outboundOnAirline[0],
    );
    // Look up the matching return leg (same Duffel offer_id).
    const pairedReturn = (data.return || []).find((r) => r.id === cheapest.id);
    if (!pairedReturn) return null;
    const pairedTotal = cheapest.price; // == offer.total_amount
    const savings = sum - pairedTotal;
    if (savings <= 0.5) return null; // ignore noise below 50¢
    return {
      outbound: cheapest,
      return: pairedReturn,
      total: pairedTotal,
      savings,
    };
  };

  const onSelect = (flight, leg) => {
    // One-way: straight to booking with the chosen flight.
    if (query.trip_type !== "round_trip") {
      const paxBreakdown = {
        adults: query.adults,
        children: query.children,
        infants: query.infants,
      };
      sessionStorage.setItem("tcp_selected_flight", JSON.stringify({
        ...flight,
        passengers: query.passengers,
        pax_breakdown: paxBreakdown,
        return_date: query.return_date,
      }));
      nav("/booking");
      return;
    }
    // Round-trip: pick outbound first, then return.
    if (leg === "outbound") {
      setSelectedOutbound(flight);
      setActiveLeg("return");
      return;
    }
    // Return picked — figure out paired vs switched-offer:
    //   • Same offer_id → paired RT (free), price already correct
    //   • Different     → user is switching to the return option's RT offer
    const paired = selectedOutbound?.id === flight.id;
    commitToBooking(selectedOutbound, flight, paired, paired ? flight.price : null);
  };

  // Round-trip selection state — drives the two-tab layout.
  const [activeLeg, setActiveLeg] = useState("outbound");
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  // Paired-RT-savings flow: when the user picks both legs on the same
  // airline, look up the cheapest paired offer covering both slices. If
  // the paired total beats their summed selection, ask before proceeding.
  const [pendingSelection, setPendingSelection] = useState(null); // {outbound, return, sum, paired?, savings?}

  const airlineList = data ? Array.from(new Set([...(data.outbound||[]), ...(data.return||[])].map(f => f.airline_code))) : [];

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      {/* ============ MOBILE HEADER (compact summary + filter strip) ============ */}
      <div className="md:hidden">
        <div className="bg-[#0A0A0B]"><Navbar dark /><div className="h-20" /></div>
        <div className="bg-white px-5 pt-4 pb-2 border-b border-[#E0DDD6]">
          <MobileSearchSummary query={query} />
          <AirlinePriceRail
            offers={data?.outbound || []}
            tripType={query.trip_type}
            selectedAirlineCode={(() => {
              // Show "active airline" only when exactly one is selected
              const onCodes = (data?.outbound || [])
                .map((o) => o.airline_code)
                .filter((c, i, a) => a.indexOf(c) === i)
                .filter((c) => airlines[c] !== false);
              return onCodes.length === 1 ? onCodes[0] : null;
            })()}
            onSelectAirline={(code) => {
              // Tapping an airline chip: solo-filter to that airline, or clear
              const next = {};
              (data?.outbound || []).forEach((o) => {
                next[o.airline_code] = code === null ? true : o.airline_code === code;
              });
              setAirlines(next);
              // Scroll to results
              requestAnimationFrame(() => {
                document.querySelector('[data-testid="mobile-results-anchor"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
          />
          <MobileFilterPills
            stops={stops}
            setStops={setStops}
            airlines={airlines}
            setAirlines={setAirlines}
            airlineList={airlineList}
            priceMax={priceMax}
            setPriceMax={setPriceMax}
            sort={sort}
            setSort={setSort}
            onSwap={() => {
              const sp = new URLSearchParams(params);
              sp.set("origin", query.destination);
              sp.set("destination", query.origin);
              nav(`/search?${sp.toString()}`);
            }}
          />
        </div>
      </div>

      {/* ============ DESKTOP HEADER (hidden on mobile) ============
          Slim header: just navbar + search widget. No big "SEARCHING / JFK→LAX"
          title — the widget already shows origin/destination chips. */}
      <div className="hidden md:block bg-[#0A0A0B] relative">
        <Navbar dark />
        <div className="px-6 md:px-12 lg:px-24 pt-24 pb-6">
          <FlightSearchWidget
            initialOrigin={query.origin}
            initialDestination={query.destination}
            initialCabin={query.cabin}
            initialTripType={query.trip_type}
            initialDepart={query.depart_date}
            initialReturn={query.return_date}
            initialAdults={query.adults}
            initialChildren={query.children}
            initialInfants={query.infants}
          />
        </div>
      </div>

      <div className="hidden md:grid px-6 md:px-12 lg:px-24 py-8 grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Filters — consolidated single-card design, sleeker spacing. */}
        <aside className="lg:col-span-3" data-testid="filters">
          <div className="bg-white border border-[#E0DDD6] sticky top-24">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E0DDD6]">
              <div className="flex items-center gap-2">
                <Funnel size={16} className="text-[#C29B62]" />
                <h3 className="font-serif text-lg text-[#1C1C1C]">Refine</h3>
              </div>
              <button
                onClick={() => {
                  setStops({ 0: true, 1: true });
                  const next = {};
                  airlineList.forEach((c) => { next[c] = true; });
                  setAirlines(next);
                  setPriceMax(5000);
                }}
                className="text-[10px] tracking-[0.16em] uppercase text-[#666460] hover:text-[#C29B62]"
                data-testid="filter-reset"
              >
                Reset
              </button>
            </div>

            {/* Stops */}
            <div className="px-5 py-4 border-b border-[#E0DDD6]">
              <p className="tcp-overline text-[#666460] mb-3">Stops</p>
              <label className="flex items-center gap-2.5 mb-2 cursor-pointer">
                <Checkbox checked={stops[0]} onCheckedChange={(v) => setStops({...stops, 0: !!v})} data-testid="filter-nonstop" />
                <span className="text-sm">Non-stop</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <Checkbox checked={stops[1]} onCheckedChange={(v) => setStops({...stops, 1: !!v})} data-testid="filter-1stop" />
                <span className="text-sm">1 stop</span>
              </label>
            </div>

            {/* Max price */}
            <div className="px-5 py-4 border-b border-[#E0DDD6]">
              <div className="flex items-center justify-between mb-3">
                <p className="tcp-overline text-[#666460]">Max Price</p>
                <p className="text-xs font-semibold text-[#1C1C1C]">{formatPrice(priceMax)}</p>
              </div>
              <Slider value={[priceMax]} max={5000} min={100} step={50} onValueChange={(v) => setPriceMax(v[0])} data-testid="filter-price" />
            </div>

            {/* Airlines */}
            <div className="px-5 py-4">
              <p className="tcp-overline text-[#666460] mb-3">Airlines</p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {airlineList.map(code => (
                  <label key={code} className="flex items-center gap-2.5 cursor-pointer">
                    <Checkbox
                      checked={airlines[code] !== false}
                      onCheckedChange={(v) => setAirlines({...airlines, [code]: !!v})}
                      data-testid={`filter-airline-${code}`}
                    />
                    <span className="text-sm">{code}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <main className="lg:col-span-9 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#666460]" data-testid="results-count">
              {loading ? "Searching..." : `${data?.outbound?.length || 0} outbound · ${data?.return?.length || 0} return`}
            </p>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="tcp-input w-auto text-xs py-2 px-3" data-testid="sort-select">
              <option value="price">Sort: Price</option>
              <option value="duration">Sort: Duration</option>
              <option value="depart">Sort: Depart Time</option>
            </select>
          </div>

          {loading && (
            <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>
          )}
          {error && <div className="p-6 bg-white border border-red-200 text-red-700" data-testid="search-error">Error: {error}</div>}

          {!loading && data && (() => {
            const filteredOutbound = filterAndSort(data.outbound);
            const filteredReturn = filterAndSort(data.return);
            const noResults = filteredOutbound.length === 0 && filteredReturn.length === 0;
            if (noResults) {
              return (
                <div
                  className="bg-[#0A0A0B] text-[#F7F5F0] p-8 md:p-12 border border-[#0A0A0B]"
                  data-testid="no-results-call-card"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <Headset size={36} weight="duotone" className="text-[#C29B62] shrink-0" />
                    <div>
                      <p className="tcp-overline text-[#C29B62] mb-2">Nothing online for this route</p>
                      <h3 className="font-serif text-3xl md:text-4xl tracking-tighter leading-tight">
                        Don't worry — we book trips like yours <em className="italic">every day.</em>
                      </h3>
                    </div>
                  </div>
                  <p className="text-[#A09E9A] max-w-2xl mb-6 leading-relaxed">
                    Some destinations and premium-cabin routings sit outside our online inventory.
                    Our travel team has access to consolidator and airline-direct fares not shown here —
                    especially for {query.origin} → {query.destination}. Give us 4 minutes on the phone and
                    we'll put together a quote and lock the price.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <a
                      href={`tel:${PHONE_TEL}`}
                      data-testid="no-results-call-btn"
                      className="btn-gold inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase"
                    >
                      <Phone size={16} weight="fill" /> Call {PHONE_DISPLAY}
                    </a>
                    <a
                      href="/contact"
                      data-testid="no-results-contact-btn"
                      className="btn-outline-light text-xs tracking-[0.16em] uppercase"
                    >
                      Request a Call-Back
                    </a>
                  </div>
                  <p className="text-xs text-[#A09E9A] mt-6">Toll-free across the United States · Open every day</p>
                </div>
              );
            }
            const isRoundTrip = query.trip_type === "round_trip" && data.return?.length > 0;
            return (
              <>
                {isRoundTrip ? (
                  <Tabs value={activeLeg} onValueChange={setActiveLeg} data-testid="results-tabs">
                    <TabsList className="grid grid-cols-2 w-full bg-white border border-[#E0DDD6] p-1 mb-6 h-auto">
                      <TabsTrigger value="outbound" data-testid="tab-outbound" className="data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-[#F7F5F0] text-xs tracking-[0.16em] uppercase py-3">
                        1 · Outbound {selectedOutbound && <span className="ml-2 text-[#C29B62]">✓</span>}
                        <span className="ml-2 text-[10px] text-[#A09E9A]">{query.depart_date}</span>
                      </TabsTrigger>
                      <TabsTrigger value="return" data-testid="tab-return" className="data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-[#F7F5F0] text-xs tracking-[0.16em] uppercase py-3">
                        2 · Return
                        <span className="ml-2 text-[10px] text-[#A09E9A]">{query.return_date}</span>
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="outbound" className="mt-0">
                      <h3 className="font-serif text-2xl text-[#1C1C1C] mb-4">
                        Choose your outbound · <span className="text-[#666460]">{query.origin} → {query.destination}</span>
                      </h3>
                      <div className="space-y-3" data-testid="outbound-list">
                        {filteredOutbound.map((f) => <FlightCard key={f.id} flight={f} onSelect={onSelect} leg="outbound" />)}
                      </div>
                    </TabsContent>

                    <TabsContent value="return" className="mt-0">
                      {/* Pinned mini-card so the user remembers the outbound they just picked. */}
                      {selectedOutbound ? (
                        <div className="bg-[#0A0A0B] text-[#F7F5F0] p-5 md:p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="selected-outbound-card">
                          <div className="flex items-center gap-4">
                            <span className="tcp-overline text-[#C29B62]">Selected outbound</span>
                            <span className="text-sm">
                              <span className="font-serif text-xl tracking-tight">{selectedOutbound.depart_time}</span>
                              <span className="text-[#A09E9A] mx-2">{selectedOutbound.origin} →</span>
                              <span className="font-serif text-xl tracking-tight">{selectedOutbound.arrive_time}</span>
                              <span className="text-[#A09E9A] ml-2">{selectedOutbound.destination}</span>
                              <span className="text-[#A09E9A] ml-3 text-xs">{selectedOutbound.airline_name} · {selectedOutbound.flight_number}</span>
                            </span>
                          </div>
                          <button
                            onClick={() => { setSelectedOutbound(null); setActiveLeg("outbound"); }}
                            data-testid="change-outbound-btn"
                            className="text-xs tracking-[0.16em] uppercase text-[#C29B62] hover:text-[#F7F5F0] border-b border-[#C29B62] pb-0.5 self-start md:self-auto"
                          >
                            Change outbound
                          </button>
                        </div>
                      ) : (
                        <div className="bg-white border border-[#E0DDD6] p-5 mb-6 text-sm text-[#666460]" data-testid="return-prompt">
                          Choose an <button onClick={() => setActiveLeg("outbound")} className="text-[#C29B62] underline">outbound flight</button> first to continue.
                        </div>
                      )}
                      <h3 className="font-serif text-2xl text-[#1C1C1C] mb-4">
                        Choose your return · <span className="text-[#666460]">{query.destination} → {query.origin}</span>
                      </h3>
                      <div className="space-y-3" data-testid="return-list">
                        {filteredReturn.map((f) => <FlightCard key={f.id} flight={f} onSelect={onSelect} leg="return" baseline={selectedOutbound?.price} baselineId={selectedOutbound?.id} />)}
                      </div>
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div>
                    <h3 className="font-serif text-2xl text-[#1C1C1C] mb-4">Outbound · {query.depart_date}</h3>
                    <div className="space-y-3" data-testid="outbound-list">
                      {filteredOutbound.map((f) => <FlightCard key={f.id} flight={f} onSelect={onSelect} leg="outbound" />)}
                    </div>
                  </div>
                )}
                <div
                  className="mt-12 bg-[#F7F5F0] border border-[#E0DDD6] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
                  data-testid="results-call-strip"
                >
                  <Headset size={28} weight="duotone" className="text-[#C29B62] shrink-0" />
                  <div className="flex-1">
                    <p className="font-serif text-xl text-[#1C1C1C] tracking-tight">Seeing what you want here?</p>
                    <p className="text-sm text-[#666460] mt-1">
                      Often a quick call beats the published fare by 10–30% — especially in business and first.
                    </p>
                  </div>
                  <a
                    href={`tel:${PHONE_TEL}`}
                    data-testid="results-call-btn"
                    className="btn-gold inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase"
                  >
                    <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
                  </a>
                </div>
              </>
            );
          })()}
        </main>
      </div>

      {/* ============ MOBILE RESULTS LIST ============ */}
      <div className="md:hidden px-5 pb-12" data-testid="mobile-results-anchor">
        {loading && (
          <div className="space-y-3 pt-5">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[140px] w-full rounded-xl" />)}
          </div>
        )}
        {error && (
          <div className="mt-5 p-4 bg-white border border-red-200 text-red-700 text-sm rounded-xl">
            Error: {error}
          </div>
        )}
        {!loading && data && (() => {
          const filteredOutbound = filterAndSort(data.outbound || []);
          const filteredReturn = filterAndSort(data.return || []);
          const noResults = filteredOutbound.length === 0 && filteredReturn.length === 0;
          if (noResults) {
            return (
              <div className="mt-5 bg-[#0A0A0B] text-[#F7F5F0] p-6 rounded-xl" data-testid="m-no-results">
                <p className="tcp-overline text-[#C29B62] mb-2">Nothing online for this route</p>
                <h3 className="font-serif text-2xl tracking-tighter leading-tight">
                  We book trips like yours <em className="italic">every day</em> — by phone.
                </h3>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="m-no-results-call"
                  className="mt-4 w-full bg-[#C29B62] text-[#0A0A0B] font-semibold text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2 py-3"
                >
                  <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
                </a>
              </div>
            );
          }

          const isRoundTrip = query.trip_type === "round_trip" && (data.return?.length || 0) > 0;
          const activeList = isRoundTrip && activeLeg === "return" ? filteredReturn : filteredOutbound;
          const tripLabel = isRoundTrip ? "Round-trip" : "One-way";

          // Tag computation: BEST = lowest weighted score, CHEAPEST = lowest price.
          const cheapest = activeList.reduce((m, f) => (!m || f.price < m.price ? f : m), null);
          const scoreOf = (f) => f.price * 0.6 + (f.duration_minutes || 0) * 0.4;
          const best = activeList.reduce((m, f) => (!m || scoreOf(f) < scoreOf(m) ? f : m), null);

          return (
            <>
              <div className="mt-5 mb-4">
                <h2 className="font-serif text-2xl text-[#1C1C1C] tracking-tight leading-tight">
                  Choose your {isRoundTrip && activeLeg === "return" ? "return" : "departing"} flight
                </h2>
                <p className="text-xs text-[#666460] mt-2 leading-relaxed">
                  Prices per person, incl. taxes &amp; fees. Additional baggage fees may apply.
                </p>
              </div>

              {/* For round-trip, show the outbound confirmation chip + leg toggle */}
              {isRoundTrip && (
                <div className="mb-4">
                  {selectedOutbound && activeLeg === "return" ? (
                    <div
                      className="bg-[#0A0A0B] text-[#F7F5F0] rounded-xl p-3 text-xs"
                      data-testid="m-outbound-confirm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex-1 min-w-0 truncate">
                          <span className="text-[#C29B62] font-semibold">✓ Outbound</span>
                          <span className="text-[#A09E9A] mx-2">·</span>
                          <span>{selectedOutbound.depart_time} {selectedOutbound.origin} → {selectedOutbound.arrive_time} {selectedOutbound.destination}</span>
                        </span>
                        <button
                          onClick={() => { setSelectedOutbound(null); setActiveLeg("outbound"); }}
                          data-testid="m-change-outbound"
                          className="text-[#C29B62] text-[10px] tracking-[0.16em] uppercase shrink-0"
                        >
                          Change
                        </button>
                      </div>
                      <div className="mt-2 pt-2 border-t border-white/10 flex items-baseline justify-between">
                        <span className="text-[10px] tracking-[0.16em] uppercase text-[#A09E9A]">Round-trip from</span>
                        <span
                          className="font-serif text-lg text-[#C29B62]"
                          data-testid="m-baseline-price"
                        >
                          {formatPrice(selectedOutbound.price)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="space-y-3" data-testid={isRoundTrip && activeLeg === "return" ? "m-return-list" : "m-outbound-list"}>
                {activeList.map((f) => (
                  <MobileFlightCard
                    key={f.id}
                    flight={f}
                    onSelect={() => onSelect(f, isRoundTrip && activeLeg === "return" ? "return" : "outbound")}
                    isBest={best && f.id === best.id}
                    isCheapest={cheapest && f.id === cheapest.id}
                    tripLabel={tripLabel}
                    baseline={isRoundTrip && activeLeg === "return" ? selectedOutbound?.price : undefined}
                    baselineId={isRoundTrip && activeLeg === "return" ? selectedOutbound?.id : undefined}
                  />
                ))}
              </div>

              <div className="mt-8 bg-[#0A0A0B] text-[#F7F5F0] rounded-xl p-5" data-testid="m-results-call-strip">
                <p className="font-serif text-lg tracking-tight">Seeing what you want?</p>
                <p className="text-xs text-[#A09E9A] mt-1">A quick call often beats published fares 10–30% in business &amp; first.</p>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="m-results-call-btn"
                  className="mt-4 w-full bg-[#C29B62] text-[#0A0A0B] font-semibold text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2 py-3"
                >
                  <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
                </a>
              </div>
            </>
          );
        })()}
      </div>

      <Footer />

      {/* Paired-RT savings prompt — surfaces a cheaper paired offer on the
          same airline before the user commits two independent legs. */}
      <AlertDialog
        open={!!pendingSelection?.paired}
        onOpenChange={(o) => { if (!o) setPendingSelection(null); }}
      >
        <AlertDialogContent
          className="max-w-lg bg-[#F7F5F0] border-[#E0DDD6]"
          data-testid="paired-savings-modal"
        >
          {pendingSelection?.paired && (() => {
            const p = pendingSelection.paired;
            const sel = pendingSelection;
            const airlineName = p.outbound.airline_name;
            return (
              <>
                <AlertDialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkle size={16} weight="fill" className="text-[#C29B62]" />
                    <span className="tcp-overline text-[#C29B62]">Cheaper paired round-trip available</span>
                  </div>
                  <AlertDialogTitle className="font-serif text-3xl tracking-tighter text-[#1C1C1C] leading-tight">
                    Save {formatPrice(p.savings)} with a paired {airlineName} ticket.
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-[#666460] text-sm leading-relaxed pt-2">
                    Both legs you picked are on <strong className="text-[#1C1C1C]">{airlineName}</strong>.
                    Booked as a single round-trip ticket on this carrier, the same itinerary is{" "}
                    <strong className="text-[#1C1C1C]">{formatPrice(p.savings)} cheaper</strong> than
                    pairing two one-ways.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="grid grid-cols-2 gap-3 my-4" data-testid="paired-comparison">
                  <div className="bg-white border border-[#E0DDD6] p-4">
                    <p className="tcp-overline text-[#666460] mb-1">Your selection</p>
                    <p className="font-serif text-3xl text-[#1C1C1C] tracking-tighter">
                      {formatPrice(sel.sum)}
                    </p>
                    <p className="text-[11px] text-[#666460] mt-1">two one-way fares</p>
                  </div>
                  <div className="bg-[#0A0A0B] text-[#F7F5F0] p-4">
                    <p className="tcp-overline text-[#C29B62] mb-1">Paired round-trip</p>
                    <p className="font-serif text-3xl tracking-tighter" style={{ color: "#C29B62" }}>
                      {formatPrice(p.total)}
                    </p>
                    <p className="text-[11px] text-[#A09E9A] mt-1">single {airlineName} ticket</p>
                  </div>
                </div>

                <AlertDialogFooter className="gap-2">
                  <AlertDialogCancel
                    onClick={() => {
                      const c = pendingSelection;
                      setPendingSelection(null);
                      commitToBooking(c.outbound, c.return);
                    }}
                    data-testid="paired-keep-original"
                    className="text-xs tracking-[0.16em] uppercase border-[#E0DDD6] text-[#666460] hover:text-[#1C1C1C] hover:border-[#1C1C1C]"
                  >
                    Keep my selection
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      const c = pendingSelection;
                      setPendingSelection(null);
                      commitToBooking(c.paired.outbound, c.paired.return, true, c.paired.total);
                    }}
                    data-testid="paired-switch-cta"
                    className="btn-gold text-xs tracking-[0.16em] uppercase inline-flex items-center gap-2"
                  >
                    Switch &amp; save {formatPrice(p.savings)} <ArrowRight size={14} />
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
